/**
 * v4Validate — Phase 5e-1.
 *
 * Pure analyzer for V4 canvas payloads. Returns a list of issues +
 * aggregate stats; no React, no side effects, no DOM access. Safe to
 * call from a useMemo on every payload change.
 *
 * Issue shape:
 *   {
 *     id:       string             // stable per-issue id for React keys
 *     severity: 'error'|'warning'|'info'
 *     code:     string             // machine code (e.g. 'MISSING_START')
 *     message:  string             // human-readable, present-tense
 *     nodeId?:  string             // node the issue points at (canvas id, not rfNode id)
 *     edgeId?:  string             // edge the issue points at
 *     suggest?: string             // optional one-line fix hint
 *   }
 *
 * Stats shape:
 *   { error: N, warning: N, info: N, total: N }
 *
 * Severity policy:
 *   error   → workflow can't run as authored (no trigger, cycle,
 *             required config field empty, duplicate node id)
 *   warning → workflow runs but author should review (orphan node,
 *             unreachable, dangling branch on a switch/fork, self-loop)
 *   info    → opportunity / suggestion (e.g. node has no label)
 *
 * RC-19e: deliberately NO "missing end" and NO "dead-end" rules.
 * Per operator design (see TRIGGER_TYPES header comment), END is a
 * visual sentinel only and runtime termination is no-outgoing-edge.
 * Flagging these as problems was producing false-positive errors
 * against perfectly valid workflows.
 *
 * NOT exhaustive — first pass covers the common-case shape errors that
 * V3 lets through. Easy to extend per node type as the V4 surface grows.
 */

/* ──────────────────────────────────────────────────────────────────
 *  OPERATOR DESIGN PRINCIPLE — START/END are visual sentinels only
 *  ──────────────────────────────────────────────────────────────────
 *
 *  This validator was originally written assuming workflows must have
 *  an explicit END (or TERMINATE) sentinel for the runtime to know
 *  when execution finishes. That assumption is WRONG for this platform.
 *
 *  Per the operator decision (see memory.feedback_start_end_sentinels,
 *  task #177 sentinel cleanup, task #182 RC-5):
 *    • START / END nodes are visual sentinels in the canvas chrome.
 *      They are NOT concealed trigger/terminate nodes. The user
 *      authors triggers explicitly (manual_start, webhook, schedule,
 *      etc.) and the runtime terminates each branch when it reaches a
 *      node with no outgoing edge — regardless of whether an "end"
 *      node is present.
 *    • A `terminate` node IS authored and real — it's the explicit
 *      "stop with this status" mechanism. Its presence is optional.
 *    • A node with no outgoing edge is the intended termination point.
 *      It is NOT a dead-end bug.
 *
 *  Consequences for this validator:
 *    1. We do NOT flag "MISSING_END" — workflows are not required to
 *       have an end/terminate sentinel.
 *    2. We do NOT flag "DEAD_END_NODE" — having no outgoing edge is
 *       valid termination by design.
 *    3. TRIGGER_TYPES expands beyond manual_start to cover every node
 *       type the platform treats as an entry point (webhook, schedule,
 *       siem_alert, email_trigger, system_event). Otherwise those
 *       trigger-rooted workflows trip the orphan/unreachable checks.
 *    4. DANGLING_BRANCH stays — a switch/fork branch declared in config
 *       but never wired is still a real authoring error.
 * ──────────────────────────────────────────────────────────────── */

// RC-112b — import NODE_TYPES so the drift validator at the bottom
// of this file can assert LEGACY_REQUIRED_CONFIG field names against
// the schemas. Same single-source-of-truth pattern as RC-100's
// buildPickerGroups. Without this, validator field names silently
// drift from schema field names and every AI-fix loop becomes
// terminal (RC-112 was the symptom).
import { NODE_TYPES } from '../../data/soarNodes';
import { validateFormTaskAssignment } from '../shared/formTaskAssignmentValidator';
import { branchDisplayName, branchIsWired } from '../../utils/canvas/branchIdentity';
import {
  LEGACY_REQUIRED_CONFIG,
  schemaFieldName,
  missingRequiredConfig,
} from '../../utils/canvas/requiredConfig';

/* Trigger / entry node types — any one of these is a valid workflow
 * root. A workflow must have AT LEAST ONE trigger (otherwise the
 * runtime never starts), but multiple triggers are allowed when the
 * workflow exposes more than one entry channel (e.g. manual_start +
 * webhook for the same canvas). */
const TRIGGER_TYPES = new Set([
  'manual_start',
  'start',
  'webhook',
  'schedule',
  'siem_alert',
  'email_trigger',
  'system_event',
]);
/* Back-compat alias for any existing reference inside this module. */
const ENTRY_TYPES = TRIGGER_TYPES;

/* End-of-flow node types. END is visual-only (operator principle);
 * TERMINATE is real but optional. Kept as a Set so the validator
 * still recognises them when computing "is this a sentinel?" for the
 * label-check skip + orphan filter — without making them required. */
const END_TYPES = new Set(['end', 'terminate']);

/* Per-node-type required config fields. Empty string + undefined +
 * null + empty-object all count as "missing". For branch heads
 * (switch_case, parallel_fork, fork) we also validate that the
 * branches/cases array isn't empty AND each branch has a name.
 *
 * RC-112a — Field names AUDITED 2026-06-09 against soarNodes.js
 * configSchemas. Pre-RC-112a values were drifted from the schemas
 * (`legs` vs `branches`, `approverRoles` vs `approvers`, `prompt` vs
 * `userPromptTemplate`, `workerKey` vs `taskDefName`, `recipient` vs
 * `recipients`, `connector` vs `connectorId`, `policy_gate` not even a
 * real node type). The drift made the AI-fix loop terminal: validator
 * emitted "missing X", AI added config.X, schema rejected X because
 * it doesn't declare X, validator re-emitted "missing X" → permanent
 * red error on properly-authored workflows.
 *
 * RC-112b — A drift validator at the bottom of this file walks each
 * entry below and asserts the field name actually exists in
 * NODE_TYPES[type].configSchema, throwing in dev/test on mismatch so
 * future divergence is impossible. */

/**
 * Main entry. Accepts a V4 canvas payload `{ nodes, connections }`
 * and returns an analysis report.
 */
export function validateWorkflow(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];
  const issues = [];

  // Empty graph short-circuit — caller's empty-state surface explains the
  // "no workflow loaded" case; validator stays quiet.
  if (nodes.length === 0) {
    return { issues: [], stats: emptyStats() };
  }

  // Maps used by multiple checks
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const outEdges = new Map(); // nodeId → connections[]
  const inEdges = new Map();
  /* Cycle-only adjacency: same as outEdges MINUS designed back-edges.
   *
   * `loop_back` (Do-While iteration) and `cognitive_return` (cognitive-loop tool return,
   * AGENT_EXECUTION_MODEL §4/§7) are intentional back-edges — they are how a Do-While and a
   * cognitive reasoning loop are expressed on the canvas. Counting them made Check 5 report
   * CYCLE_DETECTED on every cognitive agent (and every Do-While), telling the operator their
   * agent's tool-calling loop was malformed. Mirrors AUX_SOURCE_HANDLES (utils/ir/flatToIR.js)
   * and BACK_EDGE_PORTS (backend WorkflowDraftValidator / useCanvas). Reachability + orphan
   * checks keep using the FULL outEdges — only acyclicity is exempted. A genuine cycle on
   * default/structural edges still errors. */
  const cycleOutEdges = new Map();
  const isBackEdge = (c) => c && (c.fromPort === 'loop_back' || c.fromPort === 'cognitive_return'
    || c.sourceHandle === 'loop_back' || c.sourceHandle === 'cognitive_return');
  connections.forEach((c) => {
    if (!c?.from || !c?.to) return;
    if (!outEdges.has(c.from)) outEdges.set(c.from, []);
    outEdges.get(c.from).push(c);
    if (!inEdges.has(c.to)) inEdges.set(c.to, []);
    inEdges.get(c.to).push(c);
    if (!isBackEdge(c)) {
      if (!cycleOutEdges.has(c.from)) cycleOutEdges.set(c.from, []);
      cycleOutEdges.get(c.from).push(c);
    }
  });

  // ── Check 1 — Trigger presence ─────────────────────────────────────
  //
  // The workflow needs at least ONE trigger node (manual_start /
  // webhook / schedule / siem_alert / email_trigger / system_event)
  // — otherwise the runtime has nothing to bind to and the workflow
  // will never fire. Multiple triggers are explicitly allowed (a
  // canvas can expose more than one entry channel onto the same
  // logic), so we do NOT flag "multiple triggers" as a problem.
  //
  // NOTE: No "MISSING_END" check here. Per the operator's design
  // principle (see the header comment + memory.feedback_start_end_sentinels)
  // END is visual-only and TERMINATE is optional — runtime
  // termination is determined by no-outgoing-edge, not by sentinel
  // presence. Flagging "missing end" was producing false-positive
  // errors against perfectly valid workflows.
  const triggerNodes = nodes.filter((n) => TRIGGER_TYPES.has(n.type));

  if (triggerNodes.length === 0) {
    issues.push({
      id: 'missing-trigger',
      severity: 'error',
      code: 'MISSING_TRIGGER',
      message:
        'Workflow has no trigger node. Every workflow needs at least one trigger '
        + '(Manual Start, Webhook, Schedule, SIEM Alert, Email Trigger, or System Event) '
        + 'so the runtime knows what fires it.',
      suggest: 'Add a trigger from the Triggers & Events palette section.',
    });
  }

  // ── Check 2 — Duplicate node ids (defensive — useCanvas shouldn't allow this) ──
  const seenIds = new Map();
  nodes.forEach((n) => {
    if (seenIds.has(n.id)) {
      issues.push({
        id: `duplicate-id-${n.id}`,
        severity: 'error',
        code: 'DUPLICATE_NODE_ID',
        nodeId: n.id,
        message: `Two nodes share the id ${n.id}. This will cause undefined behavior at runtime.`,
        suggest: 'Delete one of the duplicates.',
      });
    } else {
      seenIds.set(n.id, n);
    }
  });

  // ── Check 3 — Self-loops ───────────────────────────────────────────
  connections.forEach((c) => {
    if (c.from && c.to && c.from === c.to) {
      issues.push({
        id: `self-loop-${c.id || `${c.from}-${c.to}`}`,
        severity: 'warning',
        code: 'SELF_LOOP',
        nodeId: c.from,
        edgeId: c.id,
        message: `Node has an edge to itself. Self-loops are rarely intentional outside of explicit retry constructs.`,
        suggest: 'Remove the self-edge or replace with a do_while head.',
      });
    }
  });

  // ── Check 4 — Orphan + reachability ────────────────────────────────
  //
  // - Orphan:      no incoming AND no outgoing edges, AND not a trigger.
  // - Unreachable: not on any path from any trigger node.
  //
  // We deliberately DO NOT flag "dead-end" nodes (those without an
  // outgoing edge). Per the operator's design principle, a node with
  // no outgoing edge IS the termination point — that's how the
  // runtime knows the branch is done (#182 RC-5 makes this explicit
  // in the runtime DAG executor). Flagging dead-ends was producing
  // false-positive warnings against every workflow that doesn't
  // funnel into a single visual End node.
  //
  // Triggers (TRIGGER_TYPES) and end-sentinels (END_TYPES) are
  // excluded from the orphan + unreachable checks because their
  // shape is intentionally different — a trigger has no upstream
  // edges by definition; an End sentinel has no downstream edges.
  const reachable = bfs(triggerNodes.map((n) => n.id), outEdges);

  nodes.forEach((n) => {
    const isSentinel = TRIGGER_TYPES.has(n.type) || END_TYPES.has(n.type);
    const hasIn = (inEdges.get(n.id) || []).length > 0;
    const hasOut = (outEdges.get(n.id) || []).length > 0;

    if (!isSentinel && !hasIn && !hasOut) {
      issues.push({
        id: `orphan-${n.id}`,
        severity: 'warning',
        code: 'ORPHAN_NODE',
        nodeId: n.id,
        message: `Node "${n.label || n.type}" has no connections — it will never execute.`,
        suggest: 'Wire it up or delete it.',
      });
      return; // skip the more specific checks for fully-orphan nodes
    }
    if (!isSentinel && !reachable.has(n.id)) {
      issues.push({
        id: `unreachable-${n.id}`,
        severity: 'warning',
        code: 'UNREACHABLE_FROM_TRIGGER',
        nodeId: n.id,
        message: `Node "${n.label || n.type}" is not reachable from any trigger.`,
        suggest: 'Add an incoming edge from a node downstream of a trigger, or remove the node.',
      });
    }
  });

  // ── Check 5 — Cycle detection (Kahn-style topological sort fails on cycles) ──
  // Runs over cycleOutEdges so designed back-edges (loop_back / cognitive_return) don't
  // masquerade as malformed cycles. Accidental cycles on structural edges still error.
  const cycleNodes = findCycleNodes(nodes, cycleOutEdges);
  if (cycleNodes.size > 0) {
    // Group all cycle nodes under a single issue so the panel doesn't get noisy
    issues.push({
      id: 'cycle',
      severity: 'error',
      code: 'CYCLE_DETECTED',
      // Point the issue at the first node in the cycle for jump-to-node
      nodeId: Array.from(cycleNodes)[0],
      message: `Cycle detected involving ${cycleNodes.size} node(s): ${Array.from(cycleNodes)
        .map((id) => nodeById.get(id)?.label || nodeById.get(id)?.type || id)
        .slice(0, 4)
        .join(', ')}${cycleNodes.size > 4 ? ', …' : ''}.`,
      suggest: 'Workflows must be acyclic. Replace with a loop / do_while head if iteration was intended.',
    });
  }

  // ── Check 6 — Dangling branches on switch/fork heads ───────────────
  // A switch_case head whose config.cases defines branches but has no
  // outgoing edges from those branch ports. Same for parallel_fork.branches.
  //
  // RC-112d — Fixed a second field-name mismatch in this check. Pre-RC-112d
  // it read `cfg.legs` for parallel_fork/fork; schema declares the field as
  // `branches`. So `legs` was always empty, branches.length===0 → early
  // return → DANGLING_BRANCH never fired on parallel_fork — silently
  // disabling this check for the entire history of parallel_fork.
  // Operators have been missing real "branch declared, no downstream node"
  // authoring bugs as a result. Now reads `cfg.branches` like the schema.
  // Kept `cfg.legs` as fallback for back-compat with any saved canvas that
  // happened to use the wrong field name during the drift window.
  nodes.forEach((n) => {
    const cfg = n.config || {};
    const cases = Array.isArray(cfg.cases) ? cfg.cases : [];
    // RC-112d — primary is `branches` (matches schema); `legs` retained
    // as legacy fallback for any in-flight canvases authored when the
    // FE was still using the wrong field name.
    const forkBranches = Array.isArray(cfg.branches) ? cfg.branches
                       : Array.isArray(cfg.legs) ? cfg.legs
                       : [];
    const branches = n.type === 'switch_case' ? cases
                   : (n.type === 'parallel_fork' || n.type === 'fork') ? forkBranches
                   : [];
    if (branches.length === 0) return;
    const out = outEdges.get(n.id) || [];
    branches.forEach((b) => {
      // Schema-aware branch identifier resolution.
      //
      // A case can be specified as a plain string OR as an object with
      // any of {label, value, name}. The pre-fix logic picked
      // `label || value || name` (label-first), then checked if any
      // outgoing edge had a port matching exactly that one chosen
      // identifier. That broke for the common pattern operators (and
      // the AI Generator) use:
      //   case: { label: "Critical", value: "critical" }
      //   edge: { fromPort: "critical" }            ← value, lowercase
      // Validator picked "Critical" → no edge matched → DANGLING_BRANCH
      // fired on a branch that was fully connected. AI's own analysis
      // in the operator's Workflow Builder banner confirmed this exact
      // root cause.
      //
      // Same-class bug as the fork-branch RC: hardcoded single-key
      // matching against a schema field that has multiple legitimate
      // aliases. Fix is to accept ANY of the case's identifiers, both
      // case-sensitive AND case-insensitive (covers Capitalized vs
      // lowercase divergence between the case `label` and the edge
      // `fromPort`).
      // Branch identity is shared with v4Validate and v2Lint — see
      // utils/canvas/branchIdentity.js. It was restated here, and the
      // restatement omitted `id`, which is the port the platform's own fork
      // generator emits. Every branch of every generated fork was therefore
      // reported dangling while being correctly wired.
      const displayName = branchDisplayName(b);
      if (!displayName) return;
      const hasEdge = branchIsWired(out, b);

      if (!hasEdge) {
        issues.push({
          id: `dangling-${n.id}-${displayName}`,
          severity: 'warning',
          code: 'DANGLING_BRANCH',
          nodeId: n.id,
          message: `Branch "${displayName}" on ${n.type} "${n.label || n.id}" has no downstream node — execution along this branch will halt.`,
          suggest: `Drop a node into the "${displayName}" branch container, or remove the branch from config.`,
        });
      }
    });
  });

  // ── Check 7 — Per-node-type required config completeness ───────────
  //
  // RC-27 — A field entry of the form "primary|alias1|alias2" is
  // treated as a satisfied requirement when ANY of the named keys is
  // present and non-missing on the node's config. This lets the same
  // rule cover historical schema drift (e.g. terminate's status key
  // is `terminationStatus` in current bootstraps but the legacy alias
  // `status` is still accepted by the runtime).
  nodes.forEach((n) => {
    missingRequiredConfig(n).forEach(({ field, severity }) => {
      issues.push({
        id: `config-${n.id}-${field}`,
        severity,
        code: 'REQUIRED_CONFIG_MISSING',
        nodeId: n.id,
        message: `${humanizeType(n.type)} "${n.label || n.id}" is missing required config: ${field}.`,
        suggest: `Open the node in the Inspector and fill in ${field}.`,
      });
    });
  });

  // ── Check 7b — form_task assignment shape (RC-220) ─────────────────
  //
  // form_task can opt into a polymorphic assignment block (RC-156 introduced
  // it, RC-219 extended to 5 modes). The validator lives in a shared module
  // so v4Validate runs the identical rules — see
  // shared/formTaskAssignmentValidator.js for the full contract.
  nodes.forEach((n) => validateFormTaskAssignment(n, issues));

  // ── Check 8 — Info-level: nodes without labels (nudge for clarity) ──
  nodes.forEach((n) => {
    if (ENTRY_TYPES.has(n.type) || END_TYPES.has(n.type)) return;
    if (!n.label || !String(n.label).trim()) {
      issues.push({
        id: `no-label-${n.id}`,
        severity: 'info',
        code: 'NODE_WITHOUT_LABEL',
        nodeId: n.id,
        message: `${humanizeType(n.type)} node has no label. Labels appear in audit trails and execution overlays.`,
        suggest: 'Click the node title in the Inspector to rename.',
      });
    }
  });

  return { issues, stats: tallyStats(issues) };
}


// ──────────────────────────────────────────────────────────────────────
// Helpers (kept un-exported — internal to the validator)
// ──────────────────────────────────────────────────────────────────────

function emptyStats() {
  return { error: 0, warning: 0, info: 0, total: 0 };
}

function tallyStats(issues) {
  const s = emptyStats();
  issues.forEach((i) => {
    if (s[i.severity] != null) s[i.severity]++;
  });
  s.total = issues.length;
  return s;
}

/** BFS forward from a set of seed node ids over the outEdges adjacency. */
function bfs(seeds, outEdges) {
  const visited = new Set();
  const queue = [...seeds];
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    (outEdges.get(id) || []).forEach((e) => {
      if (e.to && !visited.has(e.to)) queue.push(e.to);
    });
  }
  return visited;
}

/* reverseBfs() helper was removed in RC-19e — it backed the
 * DEAD_END_NODE check that we deleted per the operator principle.
 * If a future check needs upstream reachability, restore it from
 * git history rather than reinventing. */

/**
 * Returns the set of node ids that participate in at least one cycle.
 * Uses Kahn's topological sort: any node NOT visited by Kahn is part
 * of a cycle (or downstream of one). For our purposes that's the right
 * shape — the panel just needs to know which nodes to highlight.
 */
function findCycleNodes(nodes, outEdges) {
  const indegree = new Map();
  nodes.forEach((n) => indegree.set(n.id, 0));
  nodes.forEach((n) => {
    (outEdges.get(n.id) || []).forEach((e) => {
      if (e.to != null) {
        indegree.set(e.to, (indegree.get(e.to) || 0) + 1);
      }
    });
  });
  const queue = nodes.filter((n) => (indegree.get(n.id) || 0) === 0).map((n) => n.id);
  const visited = new Set();
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    (outEdges.get(id) || []).forEach((e) => {
      if (e.to == null) return;
      indegree.set(e.to, (indegree.get(e.to) || 0) - 1);
      if ((indegree.get(e.to) || 0) === 0) queue.push(e.to);
    });
  }
  const cycleNodes = new Set();
  nodes.forEach((n) => { if (!visited.has(n.id)) cycleNodes.add(n.id); });
  return cycleNodes;
}

/** A config value counts as "missing" when it's nullish, an empty string, or an empty array. */

/** Pretty-print a node type for human messages. e.g. 'http_task' → 'HTTP task'. */
function humanizeType(type) {
  if (!type) return 'Node';
  const known = {
    http_task: 'HTTP task',
    llm_task: 'LLM task',
    worker_task: 'Worker task',
    condition: 'Condition',
    switch_case: 'Switch',
    script_task: 'Script task',
    notification_task: 'Notification',
    parallel_fork: 'Parallel fork',
    fork: 'Fork',
    approval_gate: 'Approval gate',
    human_task: 'Human task',
    form_task: 'Form task',
    connector_task: 'Connector task',
    terminate: 'Terminate',
    end: 'End',
    manual_start: 'Start',
    start: 'Start',
  };
  return known[type] || type.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// ============================================================================
// RC-112b — DRIFT VALIDATOR
// ============================================================================
//
// Runs once at module init. Walks every LEGACY_REQUIRED_CONFIG entry and
// asserts the declared field name(s) actually exist in the corresponding
// NODE_TYPES[type].configSchema. Catches the exact class of bug that
// produced RC-112's terminal "Ask AI to fix" loop — validator demanding
// `legs` while schema declared `branches`, AI adding `legs`, schema
// dropping `legs`, validator re-demanding `legs`, forever.
//
// Pattern mirrors RC-100's buildPickerGroups validator:
//   - Throws in dev/test (NODE_ENV !== 'production') so regressions fail
//     the build / blow up Jest immediately with a precise repro.
//   - console.error in prod so production users don't see a blank canvas
//     just because someone shipped a field-name typo. Worst case the
//     validator emits a slightly-wrong error message — not a UI crash.
//
// To add a new node type to LEGACY_REQUIRED_CONFIG:
//   1. The field name on the LHS MUST match a `field` in the schema's
//      configSchema array.
//   2. Use the `|` pipe convention for "any of these field names is OK"
//      (currently used only by `terminate`).
//   3. Aliases (e.g. `fork` → `parallel_fork`) use the same field names
//      as their canonical — the validator doesn't dereference aliasOf
//      because aliases carry their own (often empty) configSchema and
//      we want explicit declarations.

(function validateRequiredConfigAgainstSchemas() {
  const errors = [];
  for (const [type, fields] of Object.entries(LEGACY_REQUIRED_CONFIG)) {
    const def = NODE_TYPES[type];
    if (!def) {
      errors.push(`LEGACY_REQUIRED_CONFIG['${type}']: no NODE_TYPES entry`);
      continue;
    }
    const declared = new Set(
      (def.configSchema || [])
        .map(schemaFieldName)
        .filter(Boolean),
    );
    // Aliases (e.g. fork → parallel_fork) carry an empty configSchema
    // on purpose — operators interact with the canonical, the alias
    // exists only for back-compat with imported canvases. Honour the
    // canonical's schema for drift checks.
    let effectiveDeclared = declared;
    if (declared.size === 0 && def.aliasOf && NODE_TYPES[def.aliasOf]) {
      effectiveDeclared = new Set(
        (NODE_TYPES[def.aliasOf].configSchema || [])
          .map(schemaFieldName)
          .filter(Boolean),
      );
    }
    for (const fieldSpec of fields) {
      // Pipe-syntax means "any of these alternatives is acceptable"
      const alts = String(fieldSpec).split('|');
      const anyAlt = alts.some((alt) => effectiveDeclared.has(alt));
      if (!anyAlt) {
        errors.push(
          `LEGACY_REQUIRED_CONFIG['${type}']: declared field "${fieldSpec}" not in `
          + `NODE_TYPES['${type}'].configSchema [${[...effectiveDeclared].join(', ') || '(empty)'}]`,
        );
      }
    }
  }
  if (errors.length === 0) return;
  const msg = `[v3Validate/RC-112b] LEGACY_REQUIRED_CONFIG ↔ NODE_TYPES drift detected:\n  - ${errors.join('\n  - ')}\n`
    + `Fix in v3Validate.js: rename the field on the LHS to match the schema's field name, `
    + `OR add the missing field to soarNodes.js NODE_TYPES['<type>'].configSchema.`;
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    throw new Error(msg);
  } else if (typeof console !== 'undefined') {
    console.error(msg);
  }
})();
